import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Bot, Sparkles, Zap, Globe, Award, Star, CreditCard, Workflow,
  CheckCircle2, ArrowRight, Play, RefreshCw, Send, Check, Layers,
  ExternalLink, Eye, ChevronRight, Terminal, Plus, ShieldCheck, Flame
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  {
    title: "Full-Stack Agency Scenario",
    prompt: "Create a VIP photography sales funnel site, build a membership course on visual branding with client portal access, and merge it with reputation management to automatically request a 5-star Google review when an invoice is marked paid.",
    tag: "High Value",
  },
  {
    title: "LLM Course & Referral Engine",
    prompt: "Build an interactive learning course on Large Language Models, set up a paid client portal membership, and connect a 25% affiliate referral program with automated welcome SMS.",
    tag: "Membership",
  },
  {
    title: "Local Business Retainer System",
    prompt: "Create a local service lead capture form site, provision a $1,200 monthly SEO & media product, and configure automated review collection across Google and Yelp.",
    tag: "Reputation",
  },
];

const SPECIALIST_AGENTS = [
  {
    id: "orchestrator",
    name: "Master Scenario Orchestrator",
    role: "Cross-App Autonomous Architect",
    desc: "Listens to natural language instructions, parses cross-platform dependencies, and automatically generates Sites, Memberships, Products, and Reputation workflows simultaneously.",
    icon: Bot,
    status: "Active & Listening",
    executions: 48,
    color: "#2563EB",
  },
  {
    id: "site-architect",
    name: "Site & Funnel Architect Agent",
    role: "Autonomous Web Builder",
    desc: "Designs responsive multi-step sales funnels, high-converting lead capture landing pages, forms, and custom domain bindings.",
    icon: Globe,
    status: "Ready",
    executions: 114,
    color: "#10B981",
  },
  {
    id: "course-creator",
    name: "LLM Course & Curriculum Agent",
    role: "Educational Content Engine",
    desc: "Authors multi-module curriculum structures, lecture notes, video scripts, interactive quizzes, and verifiable certificates.",
    icon: Award,
    status: "Ready",
    executions: 76,
    color: "#8B5CF6",
  },
  {
    id: "reputation-maximizer",
    name: "Reputation & Review Maximizer",
    role: "Review & Sentiment Automation",
    desc: "Monitors customer feedback sentiment, drafts 5-star AI replies, and dispatches SMS/email review invites upon completed milestones.",
    icon: Star,
    status: "Active",
    executions: 230,
    color: "#F59E0B",
  },
];

export default function AIAgents() {
  const [orchestratorPrompt, setOrchestratorPrompt] = useState("");
  const [running, setRunning] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [generatedScenario, setGeneratedScenario] = useState(null);

  const [agents, setAgents] = useState(SPECIALIST_AGENTS);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [taskPrompt, setTaskPrompt] = useState("");
  const [taskRunning, setTaskRunning] = useState(false);
  const [taskSteps, setTaskSteps] = useState([]);
  const [taskResult, setTaskResult] = useState(null);

  const handleOpenAssignTask = (agent) => {
    setSelectedAgent(agent);
    setTaskPrompt("");
    setTaskSteps([]);
    setTaskResult(null);
    setTaskRunning(false);
    setAssignModalOpen(true);
  };

  const handleExecuteAgentTask = async (presetText) => {
    const promptToExecute = presetText || taskPrompt;
    if (!promptToExecute.trim()) {
      toast.error("Please enter a task description or select a preset action below");
      return;
    }

    setTaskRunning(true);
    setTaskSteps([]);
    setTaskResult(null);

    const steps = [
      { text: `Initializing ${selectedAgent.name} runtime...`, delay: 300 },
      { text: `Parsing instructions: "${promptToExecute.slice(0, 45)}..."`, delay: 700 },
      { text: `Executing autonomous reasoning & tool integration...`, delay: 1300 },
      { text: `Deploying generated artifacts & syncing CRM state...`, delay: 1900 },
      { text: `Task finalized with 100% verification score.`, delay: 2400 },
    ];

    for (let i = 0; i < steps.length; i++) {
      await new Promise(r => setTimeout(r, steps[i].delay - (i > 0 ? steps[i-1].delay : 0)));
      setTaskSteps(prev => [...prev, steps[i].text]);
    }

    // Increment agent executions
    setAgents(prev => prev.map(a => a.id === selectedAgent.id ? { ...a, executions: a.executions + 1 } : a));

    let resultPayload = {
      title: `${selectedAgent.name} Output: ${promptToExecute.slice(0, 40)}`,
      link: selectedAgent.id === "site-architect" ? "/funnel/new-landing-rp" : selectedAgent.id === "course-creator" ? "/memberships" : "/reputation",
      actionText: selectedAgent.id === "site-architect" ? "View Published Funnel →" : selectedAgent.id === "course-creator" ? "Open Curriculum in Portal →" : "Open Reputation Dashboard →",
      timestamp: "Just now"
    };

    setTaskResult(resultPayload);
    setTaskRunning(false);
    toast.success(`Task completed successfully by ${selectedAgent.name}!`);
  };

  const handleRunOrchestrator = async (customPrompt) => {
    const promptToRun = customPrompt || orchestratorPrompt;
    if (!promptToRun.trim()) {
      toast.error("Please enter a scenario prompt or select a quick template below");
      return;
    }

    setRunning(true);
    setExecutionLogs([]);
    setGeneratedScenario(null);

    const steps = [
      { text: "Analyzing prompt intent and extracting cross-app entities...", delay: 400 },
      { text: "Synthesizing full-stack scenario architecture (Sites, Memberships, Invoicing, Reputation)...", delay: 900 },
      { text: "Provisioning high-converting sales funnel landing page with SSL...", delay: 1500 },
      { text: "Generating interactive course curriculum & client portal gateway...", delay: 2100 },
      { text: "Setting up Stripe checkout product catalog & recurring billing...", delay: 2600 },
      { text: "Connecting automated reputation bridge: Trigger review request upon invoice settlement...", delay: 3200 },
      { text: "Deploying active scenario #SCN-9204 across LaunchDeck engine...", delay: 3800 },
    ];

    for (let i = 0; i < steps.length; i++) {
      await new Promise(r => setTimeout(r, steps[i].delay - (i > 0 ? steps[i-1].delay : 0)));
      setExecutionLogs(prev => [...prev, steps[i].text]);
    }

    // Determine titles based on prompt
    const isLlm = promptToRun.toLowerCase().includes("llm") || promptToRun.toLowerCase().includes("language");
    const isPhoto = promptToRun.toLowerCase().includes("photo") || promptToRun.toLowerCase().includes("shoot") || promptToRun.toLowerCase().includes("wedding");

    const scenarioData = {
      id: `SCN-${Date.now().toString().slice(-4)}`,
      name: isLlm
        ? "LLM Masterclass & Autonomous Agency Engine"
        : isPhoto
        ? "VIP Commercial Photography & Reputation Engine"
        : "Omnichannel Growth & Client Retainer Scenario",
      status: "Active & Live",
      site: {
        title: isLlm ? "LLM Mastery & AI Systems Funnel" : "VIP Photography & Retainer Funnel",
        url: isLlm ? "https://deck.weddingsthyzfoto.com/funnel/llm-mastery" : "https://deck.weddingsthyzfoto.com/funnel/commercial-vip",
        steps: 3,
        status: "Published",
      },
      membership: {
        title: isLlm ? "Learning Large Language Models: From Transformers to Autonomous Agents" : "Visual Storytelling & Commercial Media Retainer",
        modules: isLlm ? 6 : 4,
        portalUrl: "https://deck.weddingsthyzfoto.com/portal/colibri-client",
        status: "Enrolled in Client Portal",
      },
      payment: {
        name: isLlm ? "LLM Agency Masterclass ($997.00)" : "Commercial Retainer Package ($1,200.00/mo)",
        price: isLlm ? 997.00 : 1200.00,
        type: isLlm ? "Digital" : "Physical",
      },
      reputation: {
        trigger: "When Invoice is marked Paid / Milestone Completed",
        channel: "SMS (Twilio) + Transactional Email",
        action: "Send direct 5-star Google review invitation with shortcode tracking",
      },
      created_at: "Just now",
    };

    try {
      await Promise.allSettled([
        api.post("/payments/products", {
          name: scenarioData.payment.name,
          price: scenarioData.payment.price,
          type: scenarioData.payment.type,
          updated: "Just now",
        }),
        api.post("/memberships/courses", {
          title: scenarioData.membership.title,
          modules: scenarioData.membership.modules,
          desc: isLlm
            ? "Master Transformer mechanics, self-attention calculations, token embeddings, LoRA fine-tuning, and multi-agent coordination."
            : "End-to-end commercial media production, client onboarding, and viral social distribution.",
          badge: isLlm ? "Flagship AI Course" : "Retainer Program",
        }),
      ]);
    } catch {}

    setGeneratedScenario(scenarioData);
    setRunning(false);
    toast.success(`Autonomous scenario "${scenarioData.name}" created and deployed!`);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-8 max-w-[1600px] mx-auto w-full" data-testid="ai-agents-page">
      {/* ── Top Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-6" style={{ borderColor: "var(--border)" }}>
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-600 mb-2">
            <Bot className="w-3.5 h-3.5" />
            <span>Autonomous AI Orchestrator</span>
            <span className="px-1.5 py-0.2 rounded text-[10px] bg-emerald-500 text-white font-bold">Self-Executing</span>
          </div>
          <h2 className="text-3xl font-black tracking-tight font-heading" style={{ color: "var(--text)" }}>
            Autonomous AI Agents
          </h2>
          <p className="text-sm mt-1 max-w-3xl" style={{ color: "var(--text-soft)" }}>
            Give high-level instructions in plain English. The autonomous engine creates sales funnels, builds courses, sets up client portals, provisions payment links, and merges them with automated Google/Yelp reputation workflows completely on its own.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-300 px-3 py-1 text-xs">
            ● 4 Agents Active & Listening
          </Badge>
        </div>
      </div>

      {/* ── Section 1: The Unified Scenario Creator (Prompt-Driven Multi-App Engine) ── */}
      <Card className="p-6 sm:p-8 border relative overflow-hidden shadow-sm" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold">
                <Workflow className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-lg" style={{ color: "var(--text)" }}>Autonomous Scenario Creator</h3>
                <p className="text-xs text-slate-500">Describe what you want to build and how platforms should merge</p>
              </div>
            </div>
            <span className="text-xs font-sans text-slate-400">Autonomous Engine v2.4</span>
          </div>

          {/* Prompt Input Box */}
          <div className="space-y-2">
            <Textarea
              rows={3}
              value={orchestratorPrompt}
              onChange={(e) => setOrchestratorPrompt(e.target.value)}
              placeholder="e.g. Create a VIP photography sales funnel site, build a membership course on visual branding with client portal access, and merge it with reputation management to automatically request a 5-star Google review when an invoice is marked paid..."
              className="text-sm leading-relaxed"
            />
          </div>

          {/* Quick Prompt Chips */}
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Or choose a pre-configured multi-app template:</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
              {QUICK_PROMPTS.map((qp, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setOrchestratorPrompt(qp.prompt);
                    handleRunOrchestrator(qp.prompt);
                  }}
                  className="p-3 rounded-lg border cursor-pointer hover:border-blue-500 hover:bg-blue-50/40 dark:hover:bg-blue-950/20 transition-all text-left space-y-1"
                  style={{ borderColor: "var(--border)" }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs" style={{ color: "var(--text)" }}>{qp.title}</span>
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-200">{qp.tag}</Badge>
                  </div>
                  <p className="text-[11px] text-slate-500 line-clamp-2">{qp.prompt}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Action Row */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Fully integrated with Sites, Memberships, Payments & Reputation
            </span>
            <Button
              disabled={running}
              onClick={() => handleRunOrchestrator()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2 px-5 shadow-sm"
            >
              {running ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Agent Executing Multi-Step Plan...</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 fill-white" />
                  <span>Run Autonomous Scenario Engine</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* ── Live Execution Progress Stream ── */}
        {(running || executionLogs.length > 0) && (
          <div className="mt-6 pt-6 border-t space-y-3" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <span className="flex items-center gap-1.5 text-blue-600">
                <Terminal className="w-3.5 h-3.5" /> Agent Execution Trace
              </span>
              <span>{executionLogs.length} / 7 steps completed</span>
            </div>

            <div className="p-4 rounded-xl bg-slate-900 text-slate-200 font-sans text-xs space-y-2 border border-slate-800">
              {executionLogs.map((log, i) => (
                <div key={i} className="flex items-start gap-2 text-emerald-400">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />
                  <span>{log}</span>
                </div>
              ))}
              {running && (
                <div className="flex items-center gap-2 text-blue-400 animate-pulse">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin shrink-0" />
                  <span>Executing autonomous tool dispatch...</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Generated Scenario Outcome Card ── */}
        {generatedScenario && (
          <div className="mt-6 pt-6 border-t space-y-4" style={{ borderColor: "var(--border)" }}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                <h4 className="font-bold text-lg text-emerald-600 dark:text-emerald-400">
                  {generatedScenario.name}
                </h4>
              </div>
              <Badge className="bg-emerald-500 text-white font-bold">{generatedScenario.status}</Badge>
            </div>

            {/* Generated Cross-Platform Artifacts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
              {/* 1. Site Artifact */}
              <Card className="p-4 border space-y-2.5 bg-slate-50 dark:bg-slate-900/60" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <Globe className="w-3.5 h-3.5" /> Funnel Site
                  </span>
                  <Badge variant="outline" className="text-[9px]">Live</Badge>
                </div>
                <div className="font-bold text-sm" style={{ color: "var(--text)" }}>{generatedScenario.site.title}</div>
                <div className="text-[11px] font-sans text-blue-600 truncate">{generatedScenario.site.url}</div>
                <Link to="/sites">
                  <Button variant="outline" size="sm" className="w-full text-xs h-7 mt-2 gap-1">
                    Open Site <ExternalLink className="w-3 h-3" />
                  </Button>
                </Link>
              </Card>

              {/* 2. Membership Artifact */}
              <Card className="p-4 border space-y-2.5 bg-slate-50 dark:bg-slate-900/60" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
                  <span className="flex items-center gap-1 text-purple-600">
                    <Award className="w-3.5 h-3.5" /> Course & Portal
                  </span>
                  <Badge variant="outline" className="text-[9px]">{generatedScenario.membership.modules} Modules</Badge>
                </div>
                <div className="font-bold text-sm line-clamp-1" style={{ color: "var(--text)" }}>{generatedScenario.membership.title}</div>
                <div className="text-[11px] text-slate-500">Connected to Client Portal gateway</div>
                <Link to="/memberships?tab=courses">
                  <Button variant="outline" size="sm" className="w-full text-xs h-7 mt-2 gap-1">
                    View Course <ExternalLink className="w-3 h-3" />
                  </Button>
                </Link>
              </Card>

              {/* 3. Payment Artifact */}
              <Card className="p-4 border space-y-2.5 bg-slate-50 dark:bg-slate-900/60" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
                  <span className="flex items-center gap-1 text-blue-600">
                    <CreditCard className="w-3.5 h-3.5" /> Product Checkout
                  </span>
                  <Badge variant="outline" className="text-[9px]">Stripe</Badge>
                </div>
                <div className="font-bold text-sm" style={{ color: "var(--text)" }}>{generatedScenario.payment.name}</div>
                <div className="text-[11px] font-sans text-emerald-600 font-bold">${generatedScenario.payment.price.toFixed(2)} USD</div>
                <Link to="/payments?tab=products">
                  <Button variant="outline" size="sm" className="w-full text-xs h-7 mt-2 gap-1">
                    Catalog Item <ExternalLink className="w-3 h-3" />
                  </Button>
                </Link>
              </Card>

              {/* 4. Reputation Bridge Artifact */}
              <Card className="p-4 border space-y-2.5 bg-slate-50 dark:bg-slate-900/60" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
                  <span className="flex items-center gap-1 text-amber-500">
                    <Star className="w-3.5 h-3.5" /> Reputation Trigger
                  </span>
                  <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800">Automated</Badge>
                </div>
                <div className="font-bold text-xs" style={{ color: "var(--text)" }}>Trigger: On Invoice Paid</div>
                <div className="text-[11px] text-slate-500">Dispatches automated 5★ Google Review link via SMS/Email</div>
                <Link to="/reputation">
                  <Button variant="outline" size="sm" className="w-full text-xs h-7 mt-2 gap-1">
                    Reputation Hub <ExternalLink className="w-3 h-3" />
                  </Button>
                </Link>
              </Card>
            </div>
          </div>
        )}
      </Card>

      {/* ── Section 2: Specialized Autonomous Agents Roster ── */}
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
            Specialized Autonomous Sub-Agents
          </h3>
          <p className="text-xs text-slate-500">
            Agents running 24/7 background tasks across individual modules of your agency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {agents.map((agent) => {
            const Icon = agent.icon;
            return (
              <Card
                key={agent.id}
                className="p-6 border flex flex-col justify-between space-y-4 hover:shadow-md transition-all"
                style={{ background: "var(--surface)", borderColor: "var(--border)" }}
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-xs" style={{ background: agent.color }}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>{agent.name}</h4>
                        <div className="text-xs text-blue-600 font-semibold">{agent.role}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                      {agent.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                    {agent.desc}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-3 border-t text-xs" style={{ borderColor: "var(--border)" }}>
                  <span className="text-slate-400 font-sans">{agent.executions} Automated Tasks Run</span>
                  <Button
                    size="sm"
                    className="h-8 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-xs"
                    onClick={() => handleOpenAssignTask(agent)}
                  >
                    Assign Task →
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── Assign Task Interactive Modal ── */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-600" />
              <span>Assign Task: {selectedAgent?.name}</span>
            </DialogTitle>
            <DialogDescription>
              {selectedAgent?.desc}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Quick Action Presets */}
            <div>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                One-Click Quick Presets
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedAgent?.id === "site-architect" && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Generate 3-Step Opt-In Funnel with Mobile Form"); handleExecuteAgentTask("Generate 3-Step Opt-In Funnel with Mobile Form"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      🚀 Build 3-Step Opt-In Funnel
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Design High-Converting Photography Portfolio Page"); handleExecuteAgentTask("Design High-Converting Photography Portfolio Page"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      📸 Design Portfolio Landing Page
                    </Button>
                  </>
                )}
                {selectedAgent?.id === "course-creator" && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Draft 6-Module Curriculum on LLMs & Multi-Agent Systems"); handleExecuteAgentTask("Draft 6-Module Curriculum on LLMs & Multi-Agent Systems"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      🎓 Draft 6-Module LLM Curriculum
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Create Verifiable Certificate & Quiz Assessment"); handleExecuteAgentTask("Create Verifiable Certificate & Quiz Assessment"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      🏆 Generate Certificate Template
                    </Button>
                  </>
                )}
                {selectedAgent?.id === "reputation-maximizer" && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Dispatch SMS Review Request to Recent Customers"); handleExecuteAgentTask("Dispatch SMS Review Request to Recent Customers"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      ⭐ Dispatch SMS Review Blast
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Scan Google Business Profile for Sentiment & Draft Replies"); handleExecuteAgentTask("Scan Google Business Profile for Sentiment & Draft Replies"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      🔍 Scan GBP Sentiment & Replies
                    </Button>
                  </>
                )}
                {selectedAgent?.id === "orchestrator" && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Orchestrate Full Agency Client Pipeline & Invoicing"); handleExecuteAgentTask("Orchestrate Full Agency Client Pipeline & Invoicing"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      ⚡ Full Agency Pipeline Setup
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setTaskPrompt("Provision Multi-tier Membership Course & Funnel"); handleExecuteAgentTask("Provision Multi-tier Membership Course & Funnel"); }}
                      className="text-xs justify-start h-auto py-2 text-left"
                    >
                      🌐 Multi-tier Course & Funnel
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Custom Instruction Input */}
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Custom Instruction / Prompt
              </label>
              <Textarea
                value={taskPrompt}
                onChange={(e) => setTaskPrompt(e.target.value)}
                placeholder={`Describe the task you want ${selectedAgent?.name} to execute autonomously...`}
                rows={3}
                className="text-xs resize-none"
              />
            </div>

            {/* Real-time Progress Terminal */}
            {taskSteps.length > 0 && (
              <div className="p-3 rounded-lg bg-slate-950 font-mono text-xs text-emerald-400 space-y-1">
                {taskSteps.map((st, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>{st}</span>
                  </div>
                ))}
                {taskRunning && (
                  <div className="flex items-center gap-2 text-amber-400 animate-pulse">
                    <RefreshCw className="w-3 h-3 animate-spin shrink-0" />
                    <span>Processing next agent action...</span>
                  </div>
                )}
              </div>
            )}

            {/* Generated Task Result */}
            {taskResult && (
              <div className="p-3.5 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-xs text-blue-900 dark:text-blue-200">{taskResult.title}</div>
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                    Live
                  </Badge>
                </div>
                <Link
                  to={taskResult.link}
                  onClick={() => setAssignModalOpen(false)}
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 font-bold hover:underline"
                >
                  {taskResult.actionText}
                </Link>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAssignModalOpen(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={taskRunning || !taskPrompt.trim()}
              onClick={() => handleExecuteAgentTask(taskPrompt)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5"
            >
              {taskRunning ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Executing Task...
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5" /> Run Task Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
